import { Component, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Client, ProductGroup } from '@app/core/models/client';
import { RadioBtnComponent } from '@app/shared/radio-btn/radio-btn.component';
import { VerificationField } from '@app/core/models/validation-verification';
import { URLUtils } from '@app/core/utils/url-utils';
import { JourneyService } from '@app/core/services/journey.service';
import { FallbackJourneyInfo } from '@app/core/models/fallback-journey-info';
import * as _ from 'lodash';
import { CharacterLimits } from '@app/core/utils/characterLimits';
import { ClientHeaderComponent } from '../../../../shared/client-header/client-header.component';
import { CopiedMessageComponent } from '../../../../shared/copied-message/copied-message.component';
import { RadioBtnComponent as RadioBtnComponent_1 } from '../../../../shared/radio-btn/radio-btn.component';
import { JourneyListComponent } from '../../../../journey-list/journey-list/journey-list.component';
import { NgStyle, NgClass } from '@angular/common';
import { NewGenericPhoneComponent } from '../../../../shared/new-generic-phone/new-generic-phone.component';
import { LoginPreviewComponent } from '../../../../shared/phone/sign-up-preview/sign-up-preview.component';
import { LoginFeedPreviewReactContainerComponent } from '../../../../shared/phone/sign-up-page-preview-react-container/sign-up-page-preview-react-container.component';
import { FormInputStateControlComponent } from '../../../../shared/form-input-state-control/form-input-state-control.component';
import { TinyEditorComponent } from '../../../../shared/tiny-editor/tiny-editor.component';
import { ErrorDisplayComponent } from '../../../../shared/error-display/error-display.component';
import { SafeHtmlPipePipe } from '../../../../shared/pipes/safe-html.pipe';

enum CustomerTypes {
  one = "1",
  two = "2",
  three = "3",
  four = "4",
}
@Component({
    selector: 'app-new-product-onboarding',
    templateUrl: './onboarding.component.html',
    styleUrls: ['./onboarding.component.scss'],
    standalone: true,
    imports: [ClientHeaderComponent, CopiedMessageComponent, RadioBtnComponent_1, JourneyListComponent, NgStyle, NewGenericPhoneComponent, LoginPreviewComponent, LoginFeedPreviewReactContainerComponent, FormsModule, ReactiveFormsModule, NgClass, FormInputStateControlComponent, TinyEditorComponent, ErrorDisplayComponent, SafeHtmlPipePipe]
})
export class ProductOnboardingComponent implements OnInit, OnChanges {

  @Input() client: Client;
  @Input() productGroup: ProductGroup;
  @Output() isValid: EventEmitter<boolean> = new EventEmitter<boolean>();
  validationFields: VerificationField[] = [];

  @ViewChild(RadioBtnComponent) radioBtn;

  headerGroups = { onboardingChannels: true };
  signUpGroup: UntypedFormGroup;
  isJourneySelect: boolean = false;
  showSetJourneyButton: boolean = true;
  journeyInfo: FallbackJourneyInfo;
  signUpSubmitButtonDefaultText: string = "Submit";
  termsAndConditionsDefaultText: string = "I accept the terms and conditions.";
  termsAndConditionsDefaultValidationError: string = "Please accept the terms and conditions";

  // Mock form control for express written consent validation
  expressWrittenConsentControl = new FormControl<string>('', [Validators.required]);

  constructor(private formBuilder: UntypedFormBuilder, private journeyService: JourneyService) { }

  ngOnInit() {
    this.signUpGroup = this.client.feed_enabled
      ? this.formBuilder.group({
        'message_text': new UntypedFormControl(
          this.productGroup.onboarding.sign_up_page.message_text,
          [Validators.required]
        ),
        'ccid_input_label': new UntypedFormControl(
          this.productGroup.onboarding.sign_up_page.ccid_input_label,
          [Validators.required]
        ),
        'mobile_input_label': new UntypedFormControl(
          this.productGroup.onboarding.sign_up_page.mobile_input_label,
          [Validators.required]
        ),
        'success_text': new UntypedFormControl(
          this.productGroup.onboarding.sign_up_page.success_text,
          [Validators.required, Validators.maxLength(CharacterLimits.SignUpPageSuccessCopy)]
        ),
        'failure_text': new UntypedFormControl(
          this.productGroup.onboarding.sign_up_page.failure_text,
          [Validators.required, Validators.maxLength(CharacterLimits.SignUpPageFailureCopy)]
        ),
        'submit_button_label': new UntypedFormControl(
          this.productGroup.onboarding.sign_up_page.submit_button_label ||
          this.signUpSubmitButtonDefaultText,
          [Validators.required, Validators.maxLength(CharacterLimits.SignUpPageSubmitButtonLabel)]
        ),
        'terms_and_conditions_text': new UntypedFormControl(
          this.productGroup.onboarding.sign_up_page.terms_and_conditions_text || this.termsAndConditionsDefaultText,
          [Validators.required, Validators.maxLength(CharacterLimits.SignUpPageTermsAndConditions)]
        ),
        'terms_and_conditions_validation_error': new UntypedFormControl(
          this.productGroup.onboarding.sign_up_page.terms_and_conditions_validation_error || this.termsAndConditionsDefaultValidationError,
          [Validators.required, Validators.maxLength(CharacterLimits.SignUpPageTermsAndConditionsValidationError)]
        )
      })
      : this.formBuilder.group({
        'message_text': [this.productGroup.onboarding.sign_up_page.message_text, Validators.required],
        'ccid_input_label': [this.productGroup.onboarding.sign_up_page.ccid_input_label, Validators.required],
        'mobile_input_label': [this.productGroup.onboarding.sign_up_page.mobile_input_label, Validators.required],
        'success_text': [this.productGroup.onboarding.sign_up_page.success_text, Validators.required],
        'failure_text': [this.productGroup.onboarding.sign_up_page.failure_text, Validators.required],
        'submit_button_label': [this.productGroup.onboarding.sign_up_page.submit_button_label || this.signUpSubmitButtonDefaultText, Validators.required],
        'terms_and_conditions_text': [this.productGroup.onboarding.sign_up_page.terms_and_conditions_text || this.termsAndConditionsDefaultText, Validators.required],
        'terms_and_conditions_validation_error': [this.productGroup.onboarding.sign_up_page.terms_and_conditions_validation_error || this.termsAndConditionsDefaultValidationError, Validators.required]
      });

    _.each(this.client.validation.fields, (field: VerificationField) => {
      this.signUpGroup.addControl(field.field, new FormControl<string>(field?.labels?.[this.productGroup.id] || field.prompt, [Validators.required]));
    });

    // trigger an initial 'isValid' check so subscribers get the initial value
    this.isValid.emit(this.signUpGroup.valid);

    this.setValidationFields();
    this.signUpGroup.valueChanges.subscribe(() => this.bindFormValues());

    this.initFallbackJourneySelectionContent();

    // Initialize express written consent validation
    this.updateExpressWrittenConsentValidation();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['productGroup'] && this.productGroup) {
      this.updateExpressWrittenConsentValidation();
    }
  }

  private updateExpressWrittenConsentValidation() {
    // Update the mock control based on the product group consent property
    if (this.productGroup?.consent?.express_written_consent_ts_cs) {
      this.expressWrittenConsentControl.setValue(this.productGroup.consent.express_written_consent_ts_cs);
      this.expressWrittenConsentControl.setErrors(null);
    } else {
      this.expressWrittenConsentControl.setValue('');
      this.expressWrittenConsentControl.setErrors({ required: true });
    }
    this.expressWrittenConsentControl.markAsTouched();
  }

  /**
   * 
   * @returns true if the "Customer type" for Mobile message verification is set to 4 - all other customer types should be 
   * disabled from allowing generic connections and we should hide that option accordingly
   * 
   */

  showGenericConnectionsSetting(): boolean {
    return this.client.verification.customer_type === CustomerTypes.four;
  }

  /**
   * returns true if the client has validation switched on.  Determines whether to show/hide the sign up page config fields
   */
  validationIsOn(): boolean {
    return this.client.validation.enabled === "true";
  }

  urlToEditValidation() {
    return `/client/${this.client.id}/edit/validation`;
  }

  getGeneratedURL(): string {
    const path = `/c/${this.client.id}/p/${this.productGroup.id}/sign-up`;
    if (this.productGroup.is_vanity_url_enabled) {
      return this.productGroup.vanity_url + path;
    } else {
      return URLUtils.getWireURLDefaultToProd() + path;
    }
  }

  getFormWrapperClass() {
    return this.validationIsOn() ? 'signup-form-wrapper' : 'signup-form-wrapper--hidden';
  }

  getSignupPageTipText(): string {
    if (this.validationIsOn()) {
      return 'The sign‑up page is a webform where customers can onboard to the Relay platform. In order to use the sign‑up page, validation is required.';
    } else {
      return `Validation must be switched on to configure this feature.  To change validation settings, go to "${this.urlToEditValidation()}"`;
    }
  }

  getBrandingTipText(): string {
    return 'Includes icon and \'Product Group Feed Display Name\' Banner (if enabled in Basics)'
  }

  onFallbackJourneySelection(journeyInfo: FallbackJourneyInfo) {
    this.isJourneySelect = false;
    this.journeyInfo = journeyInfo;
    this.productGroup.welcome_trigger_id = null;
    if (journeyInfo) {
      this.showSetJourneyButton = false;
      this.productGroup.welcome_trigger_id = this.journeyInfo.triggerId;
    }
  }

  removeTriggerId() {
    this.journeyInfo = new FallbackJourneyInfo();
    this.showSetJourneyButton = true;
    this.productGroup.welcome_trigger_id = null;
  }

  /**
   * When component loads, show the current fallback trigger id and
   * associated journey name, if the trigger id was previously saved.
   */
  private initFallbackJourneySelectionContent() {
    if (this.productGroup.welcome_trigger_id) {
      this.showSetJourneyButton = false;
      this.journeyInfo = new FallbackJourneyInfo();
      this.journeyInfo.triggerId = this.productGroup.welcome_trigger_id;

      this.journeyService.getJourneyByTriggerId(this.journeyInfo.triggerId, this.client.id).subscribe(
        journey => {
          this.journeyInfo.id = journey.id;
          this.journeyInfo.name = journey.latestContent.name;
        }
      );
    }
  }

  /**
   * This component is a child of a larger view.
   * The parent component uses the product group and client objects
   * to determine validity state. In order to avoid refactoring
   * we can rebind the values of the form.
   *
   * The component itself will be responsible for
   * marking fields valid/invalid
   *
   * THe parent component is responsible for allowing the entire page to submit.
   */
  private bindFormValues(): void {
    _.each(this.client.validation.fields, (field: VerificationField, i: number) => {
      if (this.signUpGroup.controls[field.field]) {
        this.client.validation.fields[i].prompt = this.signUpGroup.controls[field.field].value;

        if (this.client.feed_enabled) {
          this.client.validation.fields[i].labels = this.client.validation.fields[i].labels || {};
          this.client.validation.fields[i].labels[this.productGroup.id] = this.client.validation.fields[i].prompt;
        }

      }
    });

    this.setValidationFields();
    this.productGroup.onboarding.sign_up_page.message_text = this.signUpGroup.controls['message_text'].value;
    this.productGroup.onboarding.sign_up_page.ccid_input_label = this.signUpGroup.controls['ccid_input_label'].value;
    this.productGroup.onboarding.sign_up_page.mobile_input_label = this.signUpGroup.controls['mobile_input_label'].value;
    this.productGroup.onboarding.sign_up_page.success_text = this.signUpGroup.controls['success_text'].value;
    this.productGroup.onboarding.sign_up_page.failure_text = this.signUpGroup.controls['failure_text'].value;
    this.productGroup.onboarding.sign_up_page.submit_button_label = this.signUpGroup.controls['submit_button_label'].value;
    this.productGroup.onboarding.sign_up_page.terms_and_conditions_text = this.signUpGroup.controls['terms_and_conditions_text'].value;
    this.productGroup.onboarding.sign_up_page.terms_and_conditions_validation_error = this.signUpGroup.controls['terms_and_conditions_validation_error'].value;

    // this is legacy
    // the parent code uses this for validation
    this.isValid.emit(this.signUpGroup.valid);
  }

  setValidationFields() {
    this.validationFields = [...this.client.validation.fields];
  }

  getCharacterLimit(form: string) {
    switch (form) {
      case 'signUpSuccessCopy':
        return CharacterLimits.SignUpPageSuccessCopy;
      case 'signUpFailureCopy':
        return CharacterLimits.SignUpPageFailureCopy;
      case 'signUpPageSubmitButtonLabel':
        return CharacterLimits.SignUpPageSubmitButtonLabel;
      case 'signUpPageTermsAndConditions':
        return CharacterLimits.SignUpPageTermsAndConditions;
      case 'signUpPageTermsAndConditionsValidationError':
        return CharacterLimits.SignUpPageTermsAndConditionsValidationError;
    }
  }
}
